import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiPackage } from 'react-icons/bi';
import { FaCodeBranch, FaCreditCard, FaExchangeAlt, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { useGetUserPackagesQuery } from '@/app/services/package-rtk.service';
import TemplateCard from '@/components/common/TemplateCard';
import ApiFailure from '@/components/ui/ApiFailure';
import { PSButton } from '@/components/ui/button';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { selectUseId } from '@/features/auth/auth.slice';
import SkeletonLoading from '@/features/scenario/create/components/marketplace/SkeletonLoading';
import { resetCreateFlow, setType, toggeleCreateModal } from '@/features/scenario/create/create.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import MarketplaceEmptyState from './components/MarketplaceEmptyState';

function MarketplacePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUseId);

  const { isLoading, isError, isSuccess, refetch, isFetching, data } = useGetUserPackagesQuery();

  let content;
  if (isLoading || isFetching) {
    content = (
      <div className="tw-p-6">
        <SkeletonLoading length={6} />
      </div>
    );
  }

  if (isError)
    content = (
      <div className="tw-p-6 tw-flex tw-justify-center tw-items-center">
        <ApiFailure refetch={refetch} />
      </div>
    );

  if (isSuccess) {
    if (!data || data.length === 0) {
      content = <MarketplaceEmptyState />;
    } else {
      content = (
        <div className="tw-p-6">
          <div className="tw-space-y-6 tw-font-normal tw-font-['Lexend']">
            {data?.map(item => (
              <div
                key={item.name}
                className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-200 hover:tw-shadow-xl hover:tw-border-gray-300"
              >
                <div className="tw-flex tw-flex-col lg:tw-flex-row">
                  {/* Left Side - Package Info */}
                  <div className="tw-flex-1 tw-p-6">
                    {/* Package Header */}
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-start  md:tw-justify-between">
                      <div className="tw-flex tw-items-start tw-gap-4 tw-mb-4">
                        <div className="tw-w-12 tw-h-12 tw-bg-blue-50 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                          <span className="tw-text-2xl">📦</span>
                        </div>
                        <div className="tw-flex-1 tw-min-w-0">
                          <h3 className="tw-font-semibold tw-text-gray-900 tw-text-lg tw-line-clamp-2 tw-leading-tight tw-font-['Lexend'] tw-mb-2">
                            {item.name}
                          </h3>

                          {/* Version and Status */}
                          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                            <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-blue-50 tw-text-blue-700">
                              {t('marketplace/version')}: {item.latest_version}
                            </span>
                            <span
                              className={`tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${Utilities.renderStatusBadge(
                                item?.status
                              )}`}
                            >
                              {Utilities.renderVersionStatusTitle(item?.status, t)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="tw-text-left md:tw-text-right tw-flex tw-flex-col tw-items-start md:tw-items-end tw-gap-3 tw-font-normal tw-font-['Lexend'] tw-mb-3">
                        <div className="tw-text-gray-700 tw-text-sm">
                          {t('marketplace/payment/total')} : ${item?.total}
                        </div>
                        <div className="tw-text-gray-700 tw-text-sm">
                          {t('marketplace/payment/collectable')} : ${item?.collectable}
                        </div>
                      </div>
                    </div>

                    {/* Transaction Statistics */}
                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-mb-4">
                      <div className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3 tw-text-center">{t('marketplace/transactions/title')}</div>
                      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <div className="tw-text-center">
                          <div className={`tw-text-xl tw-font-semibold ${item?.allTransactionCount > 0 ? 'tw-text-green-600' : 'tw-text-gray-500'}`}>
                            {item.allTransactionCount}
                          </div>
                          <div className="tw-text-xs tw-text-gray-500 tw-font-medium">{t('marketplace/allTransactionCount')}</div>
                        </div>
                        <div className="tw-text-center tw-border-l tw-border-gray-200 tw-pl-4">
                          <div className={`tw-text-xl tw-font-semibold ${item?.openTransactionCount > 0 ? 'tw-text-orange-600' : 'tw-text-gray-500'}`}>
                            {item.openTransactionCount}
                          </div>
                          <div className="tw-text-xs tw-text-gray-500 tw-font-medium">{t('marketplace/openTransactionCount')}</div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3">
                      <PSButton
                        id="btn-marketplace-detail"
                        onClick={() => {
                          navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/marketplace/version-list?package_name=${item?.name}`);
                        }}
                        className="tw-flex-1 tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors tw-duration-200 tw-border-none tw-flex tw-items-center tw-justify-center tw-gap-2"
                        variant="none"
                        icon={<FaCodeBranch className="tw-w-4 tw-h-4" />}
                        title={t('marketplace/actions/viewDetails')}
                      />
                      <PSButton
                        id="btn-marketplace-transaction"
                        onClick={() => {
                          navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/marketplace/transactions?package_id=${item?.package_id}`);
                        }}
                        className="tw-flex-1 tw-bg-gray-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-700 tw-transition-colors tw-duration-200 tw-border-none tw-flex tw-items-center tw-justify-center tw-gap-2"
                        variant="none"
                        icon={<FaExchangeAlt className="tw-w-4 tw-h-4" />}
                        title={t('marketplace/actions/pendingTransactions')}
                      />
                    </div>
                  </div>

                  {/* Right Side - Package Preview */}
                  <div className="tw-w-full lg:tw-w-80 tw-bg-gray-50 tw-border-t lg:tw-border-t-0 lg:tw-border-l tw-border-gray-200 tw-p-4">
                    <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
                      <div className="tw-w-full tw-max-w-xs">
                        <TemplateCard
                          item={{
                            name: item.name,
                            package_public_name: item.package_public_name,
                            version: item.latest_version,
                            price: item.price || 0,
                            type: 'TEXTBOT',
                            package_id: item.package_id,
                            configuration: item.configuration,
                            data: item.data,
                            downloadCount: item.allTransactionCount,
                            publisher: {
                              name: userId,
                              icon: null,
                            },
                          }}
                          isSelected={false}
                          onClick={() => undefined}
                          disableAction={true}
                          className="tw-h-full"
                          variant="default"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  const showCreateButton = !isLoading && !isError && !(isSuccess && (!data || data.length === 0));

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {/* Header Section */}
        <div className="tw-mb-8 tw-grid tw-grid-cols-1 tw-gap-4">
          <div className="tw-grid tw-grid-cols-1">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
              <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900">{t('marketplace/overview/title')}</h1>
              <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold tw-bg-gradient-to-r tw-from-purple-500 tw-to-pink-500 tw-text-white tw-shadow-sm">
                {t('marketplace/betaBadge')}
              </span>
            </div>
            <p className="tw-text-gray-600">{t('marketplace/overview/description')}</p>
          </div>
          {showCreateButton && (
            <div className="tw-grid tw-justify-items-end">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-w-fit">
                <PSButton
                  id="btn-marketplace-payment"
                  onClick={() => {
                    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/marketplace/payment`);
                  }}
                  className="tw-bg-green-600 tw-text-white tw-px-6 tw-py-3 tw-rounded-lg hover:tw-bg-green-700 tw-transition-all tw-duration-200 tw-shadow-sm tw-font-semibold tw-border-none tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap"
                  variant="none"
                  icon={<FaCreditCard className="tw-w-4 tw-h-4" />}
                  title={t('marketplace/actions/earnings')}
                />
                <PSButton
                  id="btn-marketplace-create"
                  onClick={() => {
                    dispatch(resetCreateFlow());
                    dispatch(toggeleCreateModal(true));
                    dispatch(setType(BotTypeEnum.MARKETPLACE));
                  }}
                  className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-3 tw-rounded-lg hover:tw-bg-blue-700 tw-transition-all tw-duration-200 tw-shadow-sm tw-font-semibold tw-border-none tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap"
                  variant="none"
                  title={`+ ${t('marketplace/actions/createNew')}`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden tw-min-h-[600px]">{content}</div>
      </div>
    </div>
  );
}

export default WrappedErrorBoundary(MarketplacePage);
